import { CommonModule } from '@angular/common'
import { ChangeDetectorRef, Component, DestroyRef, Input, OnInit, TemplateRef, ViewChild, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { EditComponent } from '@app/modules/personas/components/edit/edit.component'
import { ShareComponent } from '@app/modules/personas/components/share/share.component'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { AuthService } from '@auth/services/auth.service'
import { ClientRepresentedEvent, EventParser, ServerRepresentedEvent, ServerRepresentedEventTiming } from '@events/models/event.model'
import { Zone } from '@events/models/zone.model'
import { EventService } from '@events/services/event.service'
import { NbCardModule, NbDialogRef, NbDialogService, NbSpinnerModule } from '@nebular/theme'
import { Persona } from '@personas/interfaces/persona.interface'
import { Observable, forkJoin, of } from 'rxjs'

/**
 * TODO: This will be removed after implementing form builder
 *
 * Loads legacy event + personas APIs and hosts {@link EditComponent} only (no micetribe sidebar / prior wizard steps).
 * Used inside {@link WizardComponent} from the v2 timeline.
 */
@Component({
  selector: 'app-legacy-persona-wizard-content',
  standalone: true,
  imports: [CommonModule, NbSpinnerModule, NbCardModule, EditComponent, ShareComponent],
  templateUrl: './legacy-persona-wizard-content.component.html',
  styleUrl: './legacy-persona-wizard-content.component.scss'
})
export class LegacyPersonaWizardContentComponent implements OnInit {
  @Input({ required: true }) public eventId!: number
  @Input({ required: true }) public personaId!: number

  @ViewChild(EditComponent) private _edit?: EditComponent

  public processing = true
  public event: ClientRepresentedEvent | null = null
  public personas: Persona[] = []
  /** Personas shown in the share dialog — the one just saved, not the full event list. */
  public sharePersonas: Persona[] = []
  public workspaceSlug = ''

  private shareDialogRef: NbDialogRef<unknown> | null = null

  private readonly _legacyEventService = inject(EventService)
  private readonly _auth = inject(AuthService)
  private readonly _wizardService = inject(WizardService)
  private readonly _dialogService = inject(NbDialogService)
  private readonly _cdr = inject(ChangeDetectorRef)
  private readonly _destroyRef = inject(DestroyRef)

  public ngOnInit(): void {
    this.workspaceSlug = this._auth.currentWorkspace?.slug ?? ''
    forkJoin([
      this._legacyEventService.getEvent(this.eventId),
      this._legacyEventService.getEventTiming(this.eventId),
      this._legacyEventService.getEventZones(this.eventId),
      this._legacyEventService.getEventForms(this.eventId)
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: ([rawEvent, timeResult, zoneResult, personaResult]: [ServerRepresentedEvent, ServerRepresentedEventTiming[], Zone[], Persona[]]) => {
          const composed = EventParser.compose(rawEvent, timeResult, zoneResult)
          this.event = EventParser.toResponse(composed)
          if (this.event.workspace_id !== this._auth.currentWorkspace.id) {
            const eventWorkspace = this._auth.workspaces.find((w) => w.id === this.event.workspace_id)
            if (eventWorkspace) {
              this._auth.currentParentWorkspace = eventWorkspace.parent ? eventWorkspace.parent_workspace : eventWorkspace
              this._auth.currentWorkspace = eventWorkspace
            }
          }
          this.personas = personaResult ?? []
          this.processing = false
          this._cdr.markForCheck()
        },
        error: () => {
          this.processing = false
          this._cdr.markForCheck()
        }
      })
  }

  public onPersonasChange(dialog: TemplateRef<unknown>, payload: { personas: Persona[]; showShareable: boolean } | Persona[]): void {
    let normalized: { personas: Persona[]; showShareable: boolean }
    if (!Object.prototype.hasOwnProperty.call(payload, 'showShareable')) {
      normalized = { personas: payload as Persona[], showShareable: true }
    } else {
      normalized = payload as { personas: Persona[]; showShareable: boolean }
    }
    this.sharePersonas = normalized.personas
    this.shareDialogRef = normalized.showShareable ? this._dialogService.open(dialog, { closeOnBackdropClick: false, closeOnEsc: false }) : null
    this._cdr.markForCheck()
  }

  public closeShareDialog(closeWizard = true): void {
    this.shareDialogRef?.close()
    this.shareDialogRef = null
    this._edit?.form?.markAsPristine()
    this._cdr.markForCheck()
    if (closeWizard) {
      this._wizardService.requestCloseAndThen(() => {})
    }
  }

  /** Overlay wizard unsaved guard */
  public hasUnsavedChanges(): boolean {
    return this._edit?.form?.dirty ?? false
  }

  public cancelPendingBlurSave(): void {}

  public revertToSaved(): void {
    this._edit?.form?.markAsPristine()
  }

  public saveUnsavedChanges(): Observable<void> {
    return of(void 0)
  }
}
